import { Injectable } from '@angular/core';
import { Item } from '../../admin/models/item';
import {StripePaymentProcessor} from "./stripe-payment-processor.service";
import {HTTPService} from "./http.service";
import {AssessmentTokenService} from "./assessment-token.service";
import {Assessment} from "../models/assessment";
import {OrderService} from "./order.service";

import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {Observable} from 'rxjs/Observable';
import { DacConfig } from 'app/shared/services/dac-config.service';

@Injectable()
export class ShoppingService {

  private _checkoutStatus:BehaviorSubject<string>;
  private _cartItems:Item[];
  private _shopItems:Item[];
  private _singleItemCheckout:boolean;
  private _singleItemCheckoutReturnUrl:string;

  constructor(
    private stripe:StripePaymentProcessor
    ,private tokenService:AssessmentTokenService
    ,private http:HTTPService
    ,private orderService:OrderService
    ,private dacConfig:DacConfig
  ) { 
    this._cartItems = [];
    this._shopItems = [];
    this._singleItemCheckout = false;
    this._checkoutStatus = new BehaviorSubject<string>("PRE_ORDER");
  }

  public get hasItems() {
    return this._cartItems && this._cartItems.length;
  }

  private hydrateItem(i:any) : Item{
    if (!i) {
      return new Item();
    }
    var subItems = [];
    if (i.subItems) {
      subItems = i.subItems.map((subItem) => this.hydrateItem(subItem))
      .filter(i => !i.assessment_uid || this.dacConfig.isAssessmentEnabled(i.assessment_uid))
    }
    let item:Item = Object.assign(new Item(), i);
    item.subItems = subItems;
    return item;
  }

  public getItemForAssessment(item:{uid:string}) {
    return this.getAvailableItems().then(items  => {
      return items.find(i => i.assessment_uid == item.uid);
    });
  }

  public getItemForAssessmentGroup(item:{id:number, name:string}) {
    return this.getAvailableItems().then(items  => {
      // can we really key off the name?? That seems risky...
      return items.find(i => i.name == item.name && i.assessment_uid == null); // I don't think these are the same...?
    });
  }

  public startSingleCheckoutOrder(checkoutReturnUrl:string) {
    this._singleItemCheckout = true;
    this._singleItemCheckoutReturnUrl = checkoutReturnUrl;
  }

  public isSingleCheckoutOrder() {
    return this._singleItemCheckout;
  }

  public endSingleItemCheckout() {
    this._singleItemCheckout = false;
    this._singleItemCheckoutReturnUrl = null;
    // if there is anything in the cart we want to empty it.
    this.emptyCart();
    this._checkoutStatus.next("ORDER_CANCELLED");
  }

  public getSingleItemCheckoutReturnUrl() {
    return this._singleItemCheckoutReturnUrl;
  }

  public getAvailableItems() : Promise<Item[]> {
    return this.http.get("/items/").then((response) => {
        let responseItems = response.json() as Item[];
        console.log(responseItems);
        return responseItems.map((i) => this.hydrateItem(i)).filter(i => !i.assessment_uid || this.dacConfig.isAssessmentEnabled(i.assessment_uid));
    });
  }

  public addItemToCart(item:Item) {
    // even though this is synchronous it could become async so we are going to promisify it.
    return new Promise((resolve, reject) => {
      let existingItem = this._cartItems.find((i) => i.id === item.id);
      if (existingItem) {
        existingItem.quantity += item.quantity;
      }
      else {
        // push a copy.
        this._cartItems.push(Object.assign(new Item(), item))
      }
      resolve();
    });
  }

  public removeItemFromCart(item:Item) {
    return new Promise((resolve, reject) => {
      this._cartItems = this._cartItems.filter((i) => i.id !== item.id);
      resolve();
    });
  }
  

  get cartItems() {
    return this._cartItems;
  }

  get cartTotal() {
    // let sum = 0.0;
    let sum = this._cartItems.reduce((a, b) => a + b.totalPrice, 0);
    return +(sum).toFixed(2); // make sure we round correctly same as on server.
  }
  
  getCheckoutStatus() : Observable<string> {
    return this._checkoutStatus;
  }
  checkout(useSavedCard:boolean) {

    let items = this._cartItems.map((i) => { return {"id": i.id, "quantity":i.quantity}; });
    this._checkoutStatus.next("ORDER_START");
    // we can't just send a raw array or else the token doesn't pass through.
    return this.http.post("/orders/", {items: items}).then((response) => {
      let order = response.json() as any;
      console.log("order created", order);
      // return order;
      if (order.total <= 0) {
        return this.handleFreeOrder(order);
      }
      else {
        return this.handlePaidOrder(order, useSavedCard);
      }
    });
  }

  handleFreeOrder(order) {
    let data = {
      pmtProcessorToken: "FREE"
      // pmtProcessorToken: "blah"
      ,orderId:order.id
    };
    return this.purchaseTokens(data);
  }

  handlePaidOrder(order, useSavedCard:boolean) {
    let amount = order.total * 100;
    this._checkoutStatus.next("PAYMENT_PREPARE");
    let stripeCharge;
    if (useSavedCard) {
      stripeCharge = Promise.resolve({orderId: order.id, useSavedCard:useSavedCard});
    } 
    else {
      stripeCharge = this.stripe.charge(amount, "Assessments")
      .then((token:string) => {
        let data = {
          pmtProcessorToken: token
          // pmtProcessorToken: "blah"
          ,orderId:order.id
          ,useSavedCard: useSavedCard
        };
        return data;
      });
    }
    return stripeCharge.then((chargeData) => {
      this._checkoutStatus.next("ORDER_FINALIZE");
      return this.purchaseTokens(chargeData);
    });
  }

  emptyCart() {
    this._cartItems = [];
  }

  private purchaseTokens(data) {
    return this.http.post("/tokens/purchase", data).then((response) => {
      this._checkoutStatus.next("ORDER_COMPLETE");
      let purchaseResult = response.json() as any;
      this.orderService.storeLastOrder(purchaseResult.order);
      this.emptyCart();
      console.log("purchase complete navigate to order page");
      return purchaseResult.order;
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/shopping.service.ts